/**
 * Event Success Page
 * Shown after successful Stripe payment for the business event.
 * Displays ticket confirmation with event details.
 */
import { useMemo } from "react";
import { useSearch } from "wouter";
import { trpc } from "@/lib/trpc";
import {
  CheckCircle2,
  Calendar,
  Clock,
  MapPin,
  Loader2,
  ArrowLeft,
  Mail,
} from "lucide-react";

export default function EventSuccess() {
  const search = useSearch();
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const token = params.get("token") || "";

  // We pass the Stripe session ID as token in the success URL
  // But the actual ticket token is in the DB — for now we show a generic success
  // The ticket email is sent automatically via webhook

  return (
    <div
      className="min-h-screen bg-[#0d0b09] text-white flex items-center justify-center p-4"
      style={{ fontFamily: "'DM Sans', sans-serif" }}
    >
      <div className="max-w-lg w-full">
        {/* Success Card */}
        <div className="bg-[#1a1510] rounded-2xl border border-[#c9a96e]/20 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#c9a96e]/20 to-[#c9a96e]/5 p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-400" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
              Registration Confirmed!
            </h1>
            <p className="text-[#b5a890]">
              Your ticket has been sent to your email
            </p>
          </div>

          {/* Ticket Details */}
          <div className="p-8">
            <div className="bg-[#12100d] rounded-xl p-6 border border-dashed border-[#c9a96e]/30 space-y-4">
              <h2 className="text-[#c9a96e] font-semibold text-lg text-center mb-4">
                Annam's Business Event
              </h2>

              <div className="flex items-center gap-3 text-[#e8dcc8]">
                <Calendar className="w-5 h-5 text-[#c9a96e] shrink-0" />
                <span>May 24, 2026</span>
              </div>
              <div className="flex items-center gap-3 text-[#e8dcc8]">
                <Clock className="w-5 h-5 text-[#c9a96e] shrink-0" />
                <span>10:00 AM – 3:00 PM</span>
              </div>
              <div className="flex items-center gap-3 text-[#e8dcc8]">
                <MapPin className="w-5 h-5 text-[#c9a96e] shrink-0" />
                <span>Near Darmstadt, Germany</span>
              </div>
            </div>

            {/* Email Notice */}
            <div className="mt-6 flex items-start gap-3 p-4 rounded-lg bg-[#c9a96e]/5 border border-[#c9a96e]/10">
              <Mail className="w-5 h-5 text-[#c9a96e] shrink-0 mt-0.5" />
              <p className="text-[#b5a890] text-sm leading-relaxed">
                Please check your email for the ticket. Save it or take a
                screenshot to present at the event.
              </p>
            </div>

            {/* Back Link */}
            <a
              href="/event"
              className="mt-6 flex items-center justify-center gap-2 text-[#c9a96e] hover:text-[#d4b87a] transition-colors text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Event Page
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
